import type { ConsentState } from "../analytics/types";
import React from "react";
import type { JSX } from "react";
import { Outlet, useLocation } from "react-router";
import { configureAnalytics, startUserSession } from "../analytics/adp";
import { buildAnalyticsConfig } from "../analytics/config";
import { trackPageView } from "../analytics/events";
import { isOAuthPath, resolvePage } from "../analytics/routes";
import { AppContext, ApplicationContext } from "../ApplicationContext";
import { useAppSelector, useAppStore } from "../redux/hooks";

/** Most pathnames kept while the consent lookup or dialog is pending; the oldest are dropped. */
const MAX_PENDING_PAGES = 20;

type SessionState = "none" | "pending" | ConsentState;

/**
 * Sends one page view per navigation to a distinct pathname, for signed-in users
 * who opted in. Sessions start once the environment is ready and the user is
 * signed in (`isLogin`, not the token, so a token refresh does not restart
 * consent). Pathnames visited while consent is pending are buffered and flushed
 * on opt-in, or dropped otherwise.
 */
export default function AnalyticsMiddleware(): JSX.Element {
    const { pathname } = useLocation();
    const { env, isEnvironmentReady } = React.useContext<AppContext>(ApplicationContext);
    const isLogin = useAppSelector((state) => state.auth.isLogin);
    const store = useAppStore();
    const config = React.useMemo(() => buildAnalyticsConfig(env), [env]);

    const [consent, setConsent] = React.useState<ConsentState | "pending">("pending");
    const lastNavigated = React.useRef<string | undefined>(undefined);
    const buffer = React.useRef<string[]>([]);

    // Signed in but the environment has not arrived yet: analytics may still turn out
    // to be enabled, so treat it as pending rather than dropping early pathnames.
    let state: SessionState = "none";
    if (isLogin) {
        if (!isEnvironmentReady) {
            state = "pending";
        } else if (config.enabled) {
            state = consent;
        }
    }

    React.useEffect(() => {
        if (!isEnvironmentReady || !config.enabled || !isLogin) {
            return undefined;
        }
        let cancelled = false;
        configureAnalytics(config);
        startUserSession(store.getState().auth.token).then((result) => {
            if (!cancelled) {
                setConsent(result);
            }
        });
        return () => {
            cancelled = true;
        };
        // Deliberately not keyed on the token or the config object: see the component comment.
    }, [isEnvironmentReady, config.enabled, isLogin]);

    React.useEffect(() => {
        if (state === "opted-in") {
            for (const pending of buffer.current) {
                trackPageView(resolvePage(pending).name);
            }
        }
        if (state !== "pending") {
            buffer.current = [];
        }
    }, [state]);

    React.useEffect(() => {
        if (pathname === lastNavigated.current) {
            return;
        }
        lastNavigated.current = pathname;
        if (isOAuthPath(pathname)) {
            return;
        }
        if (state === "opted-in") {
            trackPageView(resolvePage(pathname).name);
        } else if (state === "pending") {
            buffer.current = [...buffer.current, pathname].slice(-MAX_PENDING_PAGES);
        }
        // `state` changes are handled by the flush effect above; only a navigation reports here.
    }, [pathname]);

    return <Outlet />;
}
